<template>
  <div class="ai-chat-container">
    <!-- 对话窗口 -->
    <div v-if="isOpen" class="ai-chat-window">
      <!-- 窗口头部 -->
      <div class="chat-header">
        <h3>AI 导购助手</h3>
        <button @click="isOpen = false" class="close-btn">×</button>
      </div>
      
      <!-- 聊天记录区域 -->
      <div class="chat-messages" ref="chatMessages">
        <div 
          v-for="(message, index) in messages" 
          :key="index" 
          class="message"
          :class="message.type === 'user' ? 'user-message' : 'ai-message'"
        >
          <div class="message-content">
            {{ message.content }}
          </div>
          <div v-if="message.products && message.products.length > 0" class="product-recommendations">
            <div v-for="(product, productIndex) in message.products" :key="productIndex" class="product-card">
              <img :src="product.image" :alt="product.name" />
              <h4>{{ product.name }}</h4>
              <p>¥{{ product.price }}</p>
              <a :href="product.url" target="_blank">查看详情</a>
            </div>
          </div>
          <div v-if="message.products && message.products.length === 0" class="no-products">
            <p>暂无推荐商品，请尝试其他关键词。</p>
          </div>
        </div>
        <div v-if="isLoading" class="loading-message">
          <div class="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
      
      <!-- 输入区域 -->
      <div class="chat-input-area">
        <input 
          type="text" 
          v-model="inputMessage" 
          @keyup.enter="sendMessage" 
          placeholder="请输入您的问题..."
          class="chat-input"
        >
        <button @click="sendMessage" class="send-btn" :disabled="!inputMessage.trim() || isLoading">
          发送
        </button>
      </div>
    </div>
    
    <!-- 对话入口按钮 -->
    <button v-else @click="isOpen = true" class="chat-toggle-btn">
      <span class="chat-icon" aria-hidden="true"></span>
      <span class="chat-text">AI 导购</span>
    </button>
  </div>
</template>

<script>
import axios from 'axios';
// 动态导入 products.json 文件，确保每次都获取最新数据
export default {
  name: 'AIChat',
  data() {
    return {
      isOpen: false,
      messages: [
        {
          type: 'ai',
          content: '您好！我是AI导购助手，有什么可以帮助您的吗？'
        }
      ],
      sessionHistory: [
        { role: 'system', content: '你是一个电商平台的AI导购助手，负责回答用户的售后问题和商品相关信息。' },
        { role: 'assistant', content: '您好！我是AI导购助手，有什么可以帮助您的吗？' }
      ],
      inputMessage: '',
      isLoading: false,
      products: [],
      categoryMappingCache: {}
    };
  },
  mounted() {
    // 组件挂载时加载商品数据
    this.loadProducts();
  },
  methods: {
    // 同步商品数据
    async syncProducts() {
      try {
        // 从后端 API 获取最新商品数据
        const response = await this.$axios.get('/products/active-list');
        const products = response.data;
        
        // 转换数据格式
        const formattedProducts = products.map(product => {
          // 构建类别和标签
          let category = '其他';
          let tags = ['其他'];
          
          // 尝试从子分类和分类获取类别信息
          if (product.subCategory) {
            category = product.subCategory.name || '其他';
            tags = [category];
            if (product.subCategory.category) {
              tags.push(product.subCategory.category.name);
            }
          }
          
          return {
            id: product.id,
            name: product.name || '',
            category: category,
            tags: tags,
            price: product.price || 0,
            image: (product.imageUrl && !product.imageUrl.startsWith('/')) ? product.imageUrl : `https://picsum.photos/seed/product-${product.id}/300/300.jpg`,
            url: `/product/${product.id}`,
            stock: product.stock > 0 ? product.stock : 100,
            updated_at: product.updatedAt || new Date().toISOString(),
            is_active: product.isActive
          };
        });
        
        // 更新本地商品数据
        this.products = formattedProducts;
        
        console.log('商品数据同步成功:', formattedProducts);
        return formattedProducts;
      } catch (error) {
        console.error('同步商品数据失败:', error);
        // 同步失败时，尝试加载本地文件
        return this.loadLocalProducts();
      }
    },
    // 加载本地商品数据
    async loadLocalProducts() {
      try {
        // 从public目录获取products.json
        const response = await fetch('/products.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error('products.json not found or returned non-JSON content');
        }
        const products = await response.json();
        
        // 处理图片路径
        this.products = products.map(product => ({
          ...product,
          image: (product.image && !product.image.startsWith('/')) ? product.image : `https://picsum.photos/seed/product-${product.id}/300/300.jpg`
        }));
        
        return this.products;
      } catch (error) {
        console.error('加载本地商品数据失败:', error);
        this.products = [];
        return [];
      }
    },
    // 加载商品数据
    async loadProducts() {
      // 优先同步后端数据
      await this.syncProducts();
    },
    // 判断用户问题是否是推荐问题
    isRecommendationQuestion(userInput) {
      const input = userInput.toLowerCase();
      // 推荐相关关键词
      const recommendationKeywords = [
        '推荐', '推荐一下', '推荐个', '推荐些', '推荐点', 
        '有没有', '有什么', '什么好', '什么适合', '买什么',
        '哪个好', '哪款好', '哪些好', '推荐商品', '推荐产品'
      ];
      
      // 检查是否包含推荐相关关键词
      return recommendationKeywords.some(keyword => input.includes(keyword));
    },
    
    // 提取用户问题中的关键词
    extractKeywords(userInput) {
      const input = userInput.toLowerCase();
      // 移除常见的虚词
      const stopWords = ['我', '要', '买', '想', '用', '的', '是', '在', '有', '和', '就', '不', '人', '都', '一', '一个', '上', '也', '很', '到', '说', '要', '去', '你', '会', '着', '没有', '看', '好', '自己', '这'];
      const words = input.split(/\s+/).filter(word => word.length > 1 && !stopWords.includes(word));
      return words;
    },
    
    // 生成AI提示词
    generateAIPrompt(userInput, recommendedProducts = []) {
      const keywords = this.extractKeywords(userInput);
      const keywordStr = keywords.join('、');
      
      // 如果有推荐商品，生成包含商品信息的提示词
      if (recommendedProducts.length > 0) {
        const productInfo = recommendedProducts.map(p => 
          `- ${p.name}，价格：¥${p.price}`
        ).join('\n');
        
        return `你是一个电商平台的AI导购助手，负责根据用户需求推荐商品并生成专业的导购话术。

用户的需求："${userInput}"
用户需求中的关键词：${keywordStr}

以下是为您推荐的商品：
${productInfo}

请根据以上商品信息，生成一段友好的导购回复（不超过30字），引导用户了解推荐的商品。回复要求：
1. 语言自然友好，符合导购助手的身份
2. 提及推荐的商品名称和价格
3. 鼓励用户点击查看详情或下单
4. 不要使用固定格式的回答，要根据商品信息动态生成`;
      }
      
      // 如果没有推荐商品，生成通用导购提示词
      return `你是一个电商平台的AI导购助手，负责回答用户的问题并提供专业的商品咨询服务。

用户的问题："${userInput}"
用户问题中的关键词：${keywordStr}

请根据用户的问题，以友好、专业的语气回答，提供有价值的信息。如果用户的问题需要具体的商品信息，请引导用户提供更多细节。

回答要求：
1. 语言自然友好，符合导购助手的身份
2. 回答要具体，避免笼统的表述
3. 如果需要更多信息，请明确询问用户
4. 保持回答简洁明了，不要冗长`;
    },
    
    // 本地模拟AI回答
    generateLocalAIResponse(userInput, recommendedProducts = []) {
      const input = userInput.toLowerCase();
      const keywords = this.extractKeywords(userInput);
      
      // 如果有推荐商品，根据商品信息生成回答
      if (recommendedProducts.length > 0) {
        const firstProduct = recommendedProducts[0];
        const productNames = recommendedProducts.map(p => p.name).join('、');
        
        // 根据用户输入的关键词生成更贴合的回答
        if (input.includes('推荐') || input.includes('想要') || input.includes('想买')) {
          return `为您推荐${productNames}，其中${firstProduct.name}价格¥${firstProduct.price}，点击下方卡片查看详情吧~`;
        }
        if (input.includes('有没有') || input.includes('有没有') || input.includes('什么好')) {
          return `找到${recommendedProducts.length}款相关商品！推荐${firstProduct.name}（¥${firstProduct.price}），点击查看更多选择~`;
        }
        if (input.includes('适合')) {
          return `根据您的需求，为您精选了${productNames}，其中${firstProduct.name}性价比很高（¥${firstProduct.price}）~`;
        }
        // 默认推荐回复
        return `为您找到${recommendedProducts.length}款相关商品，推荐"${firstProduct.name}"（¥${firstProduct.price}），点击卡片查看详情吧！`;
      }
      
      // 没有推荐商品时的回答
      // 常见问候语
      if (input.includes('你好') || input.includes('hello') || input.includes('hi')) {
        return '您好！我是AI导购助手，很高兴为您服务。请问您需要了解什么商品信息？如果您需要推荐商品，可以告诉我您的需求，我会为您推荐合适的商品。';
      }
      
      // 感谢类话语
      if (input.includes('谢谢') || input.includes('感谢')) {
        return '不客气！如果您有任何其他问题，随时告诉我。';
      }
      
      // 告别类话语
      if (input.includes('再见') || input.includes('拜拜')) {
        return '再见！欢迎您再次咨询。';
      }
      
      // 价格相关问题
      if (input.includes('价格') || input.includes('多少钱')) {
        return '我们有各种价位的商品，请问您具体想了解哪类商品的价格呢？您可以告诉我商品名称或类别，我会为您提供详细信息。';
      }
      
      // 尺码相关问题
      if (input.includes('尺码') || input.includes('尺寸')) {
        return '我们的商品都有详细的尺码信息，请问您具体想了解哪款商品的尺码呢？您可以告诉我商品名称，我会为您提供详细的尺码指南。';
      }
      
      // 质量相关问题
      if (input.includes('质量') || input.includes('怎么样')) {
        return '我们的商品质量都经过严格把关，请问您具体想了解哪款商品的质量信息呢？您可以告诉我商品名称，我会为您提供详细的产品信息。';
      }
      
      // 配送相关问题
      if (input.includes('配送') || input.includes('发货') || input.includes('快递')) {
        return '我们支持多种配送方式，一般情况下下单后1-3天内发货，具体配送时间会根据您的收货地址有所不同。请问您需要了解哪款商品的具体配送信息呢？';
      }
      
      // 售后相关问题
      if (input.includes('退货') || input.includes('退款') || input.includes('售后')) {
        return '我们支持7天无理由退换货，商品质量问题可享受更长时间的售后保障。请问您需要了解具体的售后服务政策，还是有特定商品的售后问题需要咨询？';
      }
      
      // 基于关键词的回答
      if (keywords.length > 0) {
        return `您好！关于"${keywords.join('、')}"相关的信息，我很乐意为您解答。请问您具体想了解哪方面的内容呢？如果您需要推荐相关商品，可以告诉我您的具体需求。`;
      }
      
      // 默认回答
      return '您好！我是AI导购助手，请问您需要了解什么商品信息？如果您需要推荐商品，可以告诉我您的需求，我会为您推荐合适的商品。';
    },
    
    // 调用AI生成回答
    async generateAIResponse(userInput, recommendedProducts = []) {
      try {
        console.log('开始调用AI生成回答，用户输入:', userInput, '推荐商品:', recommendedProducts);
        const apiKey = 'sk-d11cc5cfeb724033883061e8f820d68d';
        const apiUrl = 'https://api.deepseek.com/v1/chat/completions';
        
        const prompt = this.generateAIPrompt(userInput, recommendedProducts);
        console.log('生成的提示词:', prompt);
        
        console.log('开始发送API请求...');
        const response = await axios.post(apiUrl, {
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: '你是一个电商平台的AI导购助手，负责回答用户的问题并提供专业的商品咨询服务。'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 150
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          }
        });
        
        console.log('API请求成功，响应数据:', response.data);
        const aiResponse = response.data.choices[0].message.content.trim();
        console.log('AI生成的回答:', aiResponse);
        return aiResponse;
      } catch (error) {
        console.error('AI生成回答失败:', error);
        // 失败时使用本地模拟AI回答
        const localResponse = this.generateLocalAIResponse(userInput, recommendedProducts);
        console.log('使用本地模拟回答:', localResponse);
        return localResponse;
      }
    },
    
    async sendMessage() {
      if (!this.inputMessage.trim() || this.isLoading) return;
      
      // 添加用户消息
      const userMessage = this.inputMessage.trim();
      this.messages.push({ type: 'user', content: userMessage });
      this.sessionHistory.push({ role: 'user', content: userMessage });
      this.inputMessage = '';
      this.scrollToBottom();
      
      try {
        this.isLoading = true;
        
        // 判断是否是推荐问题
        if (this.isRecommendationQuestion(userMessage)) {
          // 重新加载商品数据，确保使用最新数据
          await this.loadProducts();
          console.log('加载的商品数据:', this.products);
          
          // 调用AI推荐商品
          const recommendedProducts = await this.findProducts(userMessage);
          console.log('推荐的商品:', recommendedProducts);
          
          // 调用AI生成回复，传入推荐商品信息
          const aiResponse = await this.generateAIResponse(userMessage, recommendedProducts);
          
          // 添加AI回复和推荐商品
          this.messages.push({ 
            type: 'ai', 
            content: aiResponse,
            products: recommendedProducts
          });
          this.sessionHistory.push({ role: 'assistant', content: aiResponse });
        } else {
          // 不是推荐问题，调用AI生成回答
          const aiResponse = await this.generateAIResponse(userMessage);
          
          // 添加AI回复，不包含商品推荐
          this.messages.push({ 
            type: 'ai', 
            content: aiResponse
            // 不添加products字段，这样就不会显示商品卡片
          });
          this.sessionHistory.push({ role: 'assistant', content: aiResponse });
        }
      } catch (error) {
        console.error('AI接口调用失败:', error);
        
        let aiResponse;
        // 判断是否是推荐问题
        if (this.isRecommendationQuestion(userMessage)) {
          // 出错时使用简单匹配
          const recommendedProducts = this.simpleMatchProducts(userMessage);
          if (recommendedProducts.length > 0) {
            aiResponse = '为您找到以下相关商品：';
          } else {
            aiResponse = '抱歉，AI服务暂时不可用，请稍后再试。';
          }
          this.messages.push({ 
            type: 'ai', 
            content: aiResponse,
            products: recommendedProducts
          });
        } else {
          // 不是推荐问题，调用AI生成回答
          try {
            aiResponse = await this.generateAIResponse(userMessage);
            this.messages.push({ 
              type: 'ai', 
              content: aiResponse
            });
          } catch (innerError) {
            console.error('生成AI回答失败:', innerError);
            aiResponse = '您好！我是AI导购助手，请问您需要了解什么商品信息？';
            this.messages.push({ 
              type: 'ai', 
              content: aiResponse
            });
          }
        }
        this.sessionHistory.push({ role: 'assistant', content: aiResponse });
      } finally {
        this.isLoading = false;
        this.scrollToBottom();
      }
    },
    // 基于规则的商品类别识别
    ruleBasedCategoryIdentification() {
      return '';
    },
    // 商品匹配逻辑（基于AI推荐）
    async findProducts(userInput) {
      try {
        // 调用后端AI推荐API
        const response = await this.$axios.post('/ai/recommend', {
          userInput: userInput
        });
        
        const recommendedProducts = response.data;
        console.log('后端AI推荐的商品:', recommendedProducts);
        
        // 如果有推荐商品，返回它们
        if (recommendedProducts && recommendedProducts.length > 0) {
          return recommendedProducts;
        }
        
        // 如果后端没有返回推荐，使用本地匹配策略
        return this.enhancedMatchProducts(userInput);
      } catch (error) {
        console.error('后端AI推荐失败:', error);
        // 如果后端调用失败，使用本地匹配策略
        return this.enhancedMatchProducts(userInput);
      }
    },
    // 简单的商品匹配逻辑（当AI调用失败时使用）
    simpleMatchProducts(userInput) {
      const input = userInput.toLowerCase();
      
      // 过滤掉太短的输入
      if (input.length < 2) {
        return [];
      }
      
      // 基于用户输入匹配商品
      const matchedProducts = this.products.filter(p => {
        const name = p.name.toLowerCase();
        const tags = (p.tags || []).map(t => t.toLowerCase());
        const cat = p.category.toLowerCase();
        const desc = (p.description || '').toLowerCase();

        return name.includes(input) || tags.some(tag => tag.includes(input)) || cat.includes(input) || desc.includes(input);
      });
      
      return matchedProducts.slice(0, 5);
    },
    // 增强的商品匹配策略
    enhancedMatchProducts(userInput) {
      console.log('增强匹配策略 - 用户输入:', userInput);
      
      // 首先尝试使用简单匹配
      let matchedProducts = this.simpleMatchProducts(userInput);
      console.log('简单匹配结果:', matchedProducts);
      
      // 如果有匹配到商品，直接返回
      if (matchedProducts.length > 0) {
        return matchedProducts;
      }
      
      // 如果没有匹配到，使用更宽松的匹配策略
      const input = userInput.toLowerCase();
      console.log('处理后的输入:', input);
      
      // 提取关键词，过滤掉常见的虚词
      const stopWords = ['我', '要', '买', '想', '用', '的', '是', '在', '有', '和', '就', '不', '人', '都', '一', '一个', '上', '也', '很', '到', '说', '要', '去', '你', '会', '着', '没有', '看', '好', '自己', '这'];
      const inputWords = input.split(/\s+/).filter(word => word.length > 1 && !stopWords.includes(word));
      console.log('提取的关键词:', inputWords);
      
      // 特殊处理衣服相关的输入
      const clothingKeywords = ['衣服', '服装', '上衣', 'T恤', '衬衫', '外套', '卫衣', '裤子', '牛仔裤', '裙子', '连衣裙', '羽绒服', '大衣'];
      const isClothingRelated = inputWords.some(word => clothingKeywords.some(clothingWord => word.includes(clothingWord) || clothingWord.includes(word)));
      
      // 如果是衣服相关的输入，优先匹配服饰类商品
      if (isClothingRelated) {
        const clothingProducts = this.products.filter(p => {
          const name = p.name.toLowerCase();
          const tags = (p.tags || []).map(t => t.toLowerCase());
          const cat = p.category.toLowerCase();
          
          // 检查是否是衣服类商品
          return clothingKeywords.some(clothingWord => 
            name.includes(clothingWord) || 
            tags.some(tag => tag.includes(clothingWord)) || 
            cat.includes(clothingWord)
          );
        });
        
        console.log('衣服相关商品匹配结果:', clothingProducts);
        if (clothingProducts.length > 0) {
          return clothingProducts.slice(0, 5);
        }
      }
      
      // 处理保温相关的输入
      if (input.includes('保温')) {
        const thermosProducts = this.products.filter(p => {
          const name = p.name.toLowerCase();
          const tags = (p.tags || []).map(t => t.toLowerCase());
          const cat = p.category.toLowerCase();
          const desc = (p.description || '').toLowerCase();
          
          return name.includes('保温') || 
                 tags.some(tag => tag.includes('保温')) || 
                 cat.includes('保温') || 
                 desc.includes('保温');
        });
        
        console.log('保温相关商品匹配结果:', thermosProducts);
        if (thermosProducts.length > 0) {
          return thermosProducts.slice(0, 5);
        }
      }
      
      // 如果有关键词，使用关键词进行匹配
      if (inputWords.length > 0) {
        const looseMatchedProducts = this.products.filter(p => {
          const name = p.name.toLowerCase();
          const tags = (p.tags || []).map(t => t.toLowerCase());
          const cat = p.category.toLowerCase();
          const desc = (p.description || '').toLowerCase();
          
          // 检查是否有任何关键词匹配
          const isMatch = inputWords.some(word => 
            name.includes(word) || 
            tags.some(tag => tag.includes(word)) || 
            cat.includes(word) || 
            desc.includes(word)
          );
          console.log('商品:', p.name, '匹配结果:', isMatch);
          return isMatch;
        });
        
        console.log('宽松匹配结果:', looseMatchedProducts);
        // 如果宽松匹配有结果，返回
        if (looseMatchedProducts.length > 0) {
          return looseMatchedProducts.slice(0, 5);
        }
      }
      
      // 如果仍然没有匹配到，尝试基于商品类别进行更广泛的匹配
      const categoryMatchedProducts = this.products.filter(p => {
        const cat = p.category.toLowerCase();
        const isMatch = input.includes(cat) || cat.includes(input);
        console.log('商品类别匹配 - 商品:', p.name, '类别:', cat, '匹配结果:', isMatch);
        return isMatch;
      });
      
      console.log('类别匹配结果:', categoryMatchedProducts);
      // 如果类别匹配有结果，返回
      if (categoryMatchedProducts.length > 0) {
        return categoryMatchedProducts.slice(0, 5);
      }
      
      // 如果输入是数字，尝试匹配ID或名称包含该数字的商品
      if (/^\d+$/.test(userInput)) {
        const numberProducts = this.products.filter(p => 
          p.id.toString() === userInput || 
          p.name.includes(userInput)
        );
        
        console.log('数字匹配结果:', numberProducts);
        if (numberProducts.length > 0) {
          return numberProducts.slice(0, 5);
        }
      }
      
      // 如果仍然没有匹配到，返回衣服相关商品作为默认推荐
      const defaultClothingProducts = this.products.filter(p => {
        const name = p.name.toLowerCase();
        const tags = (p.tags || []).map(t => t.toLowerCase());
        const cat = p.category.toLowerCase();
        
        return clothingKeywords.some(clothingWord => 
          name.includes(clothingWord) || 
          tags.some(tag => tag.includes(clothingWord)) || 
          cat.includes(clothingWord)
        );
      });
      
      console.log('默认衣服推荐:', defaultClothingProducts);
      if (defaultClothingProducts.length > 0) {
        return defaultClothingProducts.slice(0, 5);
      }
      
      // 如果仍然没有匹配到，返回前5个商品作为默认推荐
      console.log('返回默认推荐:', this.products.slice(0, 5));
      return this.products.slice(0, 5);
    },
    async identifyProductCategory(userInput) {
      // 确保categoryMappingCache存在
      if (!this.categoryMappingCache) {
        this.categoryMappingCache = {};
      }
      
      // 检查缓存中是否已有映射
      if (this.categoryMappingCache[userInput]) {
        console.log('使用缓存的映射:', this.categoryMappingCache[userInput]);
        return this.categoryMappingCache[userInput];
      }
      
      // 实际的deepseek API调用，用于识别商品类别
      const apiKey = 'sk-52f316b5dd474a0195345a1b9809f016'; // 尝试使用新的API密钥
      const apiUrl = 'https://api.deepseek.com/v1/chat/completions';
      
      // 准备商品数据信息，包含名称、类别、标签和描述
      const productInfo = this.products.map(p => 
        `商品: ${p.name}, 类别: ${p.category}, 标签: ${p.tags.join(',')}, 描述: ${p.description || '无'}`
      ).join('\n');
      
      try {
        const response = await axios.post(apiUrl, {
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `你是一个电商平台的 AI 导购助手。你的任务是分析用户的自然语言需求，并根据商城商品信息推荐最匹配的商品类别或标签。请严格遵循以下规则：

1. **输入信息**：
   - 用户需求：用户可能描述用途、场景、功能或特性。
   - 商品信息：每个商品包含名称、类别、标签、描述和适用场景。

2. **输出要求**：
   - 只返回最匹配的商品类别或标签。
   - 输出必须简短，一词或短语即可。
   - 不允许返回默认值或笼统回答（例如“其他”、“无”）。
   - 输出内容应能直接用于商品推荐逻辑匹配。

3. **推荐逻辑示例**：
   - 用户说“通勤上班穿什么舒服”，应推荐 **衬衫** 或 **通勤外套**。
   - 用户说“天气冷了想保暖”，应推荐 **羽绒服** 或 **大衣**。
   - 用户说“运动、跑步或户外活动”，应推荐 **运动T恤** 或 **运动外套**。
   - 用户说“夏天想穿得清爽透气”，应推荐 **短袖T恤** 或 **轻薄衬衫**。
   - 用户说“想要正式一点的搭配”，应推荐 **西装外套** 或 **连衣裙**。
   - 对其他类型商品，也应根据描述、用途和场景匹配。

4. **处理未知情况**：
   - 如果用户需求明确，但匹配多种商品，返回最符合场景或用途的类别。
   - 不要返回无意义或空值。

5. **示例输出**：
   - 用户需求：“上班通勤穿搭” → 输出：“衬衫”
   - 用户需求：“跑步运动” → 输出：“运动T恤”
   - 用户需求：“冬天保暖” → 输出：“羽绒服”

6. **扩展性**：
   - 当商品库增加新商品或类别时，只需保证商品信息中有类别、标签和描述，AI 即可自动匹配。
   这个是综合商城，不是服饰商城，以上仅供逻辑参考`
            },
            {
              role: 'user',
              content: `用户需求: ${userInput}\n商品信息:\n${productInfo}`
            }
          ],
          temperature: 0.1,
          max_tokens: 20
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          }
        });
        
        const category = response.data.choices[0].message.content.trim();
        
        // 缓存映射结果
        this.categoryMappingCache[userInput] = category;
        console.log('缓存新的映射:', userInput, '->', category);
        
        return category;
      } catch (error) {
        console.error('AI识别失败', error);
        return '';
      }
    },
    matchProductByName(userInput) {
      // 尝试直接匹配商品名称
      const input = userInput.toLowerCase();
      for (const product of this.products) {
        if (input.includes(product.name.toLowerCase())) {
          return product.category;
        }
      }
      
      // 尝试基于商品描述进行匹配，优先匹配包含关键词最多的商品
      let bestMatch = null;
      let maxMatches = 0;
      
      for (const product of this.products) {
        const description = (product.description || '').toLowerCase();
        const inputWords = input.split(' ');
        const matches = inputWords.filter(word => description.includes(word)).length;
        
        if (matches > maxMatches) {
          maxMatches = matches;
          bestMatch = product;
        }
      }
      
      if (bestMatch) {
        return bestMatch.category;
      }
      
      // 尝试匹配商品类别
      for (const product of this.products) {
        if (input.includes(product.category.toLowerCase())) {
          return product.category;
        }
      }
      
      // 如果都没有匹配，返回第一个商品的类别
      return this.products.length > 0 ? this.products[0].category : '';
    },
    findProductsByCategory(category) {
      if (!category) return [];
      
      // 转换为小写，进行不区分大小写的匹配
      const categoryLower = category.toLowerCase();
      
      // 首先根据类别和标签查询商品
      const matchedProducts = this.products.filter(product => {
        const productNameLower = product.name.toLowerCase();
        const productCategoryLower = product.category.toLowerCase();
        const productTagsLower = product.tags.map(tag => tag.toLowerCase());
        
        // 匹配类别、标签或商品名称
        return productCategoryLower === categoryLower || 
               productTagsLower.includes(categoryLower) ||
               productNameLower.includes(categoryLower);
      });
      
      // 如果没有匹配的商品，尝试进行更广泛的匹配
      if (matchedProducts.length === 0) {
        // 尝试使用更宽松的匹配方式
        const looseMatches = this.products.filter(product => {
          const productNameLower = product.name.toLowerCase();
          const productCategoryLower = product.category.toLowerCase();
          const productTagsLower = product.tags.map(tag => tag.toLowerCase());
          
          // 宽松匹配：检查类别、标签或名称是否包含目标类别
          return productCategoryLower.includes(categoryLower) ||
                 productTagsLower.some(tag => tag.includes(categoryLower)) ||
                 productNameLower.includes(categoryLower);
        }).slice(0, 5);
        
        if (looseMatches.length > 0) {
          return looseMatches;
        }
      }
      
      // 如果仍然没有匹配的商品，返回所有商品
      if (matchedProducts.length === 0) {
        return this.products.slice(0, 5);
      }
      
      // 返回最多5个商品
      return matchedProducts.slice(0, 5);
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const chatMessages = this.$refs.chatMessages;
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      });
    }
  }
};
</script>

<style scoped>
.ai-chat-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.chat-toggle-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 12px 20px;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 123, 255, 0.3);
  transition: all 0.3s ease;
}

.chat-toggle-btn:hover {
  background-color: #0056b3;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.4);
}

.chat-icon {
  position: relative;
  width: 22px;
  height: 22px;
  font-size: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.16);
}

.chat-icon::before,
.chat-icon::after {
  content: '';
  position: absolute;
}

.chat-icon::before {
  top: 5px;
  left: 5px;
  width: 12px;
  height: 9px;
  border: 2px solid #fff;
  border-radius: 6px;
}

.chat-icon::after {
  bottom: 3px;
  left: 8px;
  width: 6px;
  height: 6px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: skewX(-20deg) rotate(-10deg);
}

.chat-text {
  font-size: 14px;
  font-weight: 500;
}

.ai-chat-window {
  width: 350px;
  height: 450px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  background-color: #007bff;
  color: white;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.chat-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: #f8f9fa;
}

.message {
  margin-bottom: 15px;
  max-width: 80%;
}

.user-message {
  align-self: flex-end;
  margin-left: auto;
}

.ai-message {
  align-self: flex-start;
  margin-right: auto;
}

.message-content {
  padding: 10px 15px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.4;
}

.user-message .message-content {
  background-color: #007bff;
  color: white;
  border-bottom-right-radius: 4px;
}

.ai-message .message-content {
  background-color: white;
  color: #333;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 商品推荐区域 */
.product-recommendations {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.product-card {
  background-color: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.product-card img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 8px;
}

.product-card h4 {
  margin: 0 0 5px 0;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.product-card p {
  margin: 0 0 8px 0;
  font-size: 13px;
  color: #007bff;
  font-weight: 600;
}

.product-card a {
  font-size: 12px;
  color: #007bff;
  text-decoration: none;
  padding: 4px 8px;
  border: 1px solid #007bff;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.product-card a:hover {
  background-color: #007bff;
  color: white;
}

/* 无商品推荐 */
.no-products {
  margin-top: 10px;
  padding: 10px;
  background-color: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  text-align: center;
}

.no-products p {
  margin: 0;
  font-size: 13px;
  color: #6c757d;
}

.loading-message {
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;
}

.loading-dots {
  display: flex;
  gap: 4px;
  align-items: center;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  background-color: #007bff;
  border-radius: 50%;
  animation: pulse 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes pulse {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.chat-input-area {
  display: flex;
  padding: 10px;
  border-top: 1px solid #e9ecef;
  background-color: white;
}

.chat-input {
  flex: 1;
  padding: 10px 15px;
  border: 1px solid #ced4da;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
}

.chat-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.send-btn {
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  margin-left: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.send-btn:hover:not(:disabled) {
  background-color: #0056b3;
  transform: scale(1.05);
}

.send-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* 滚动条样式 */
.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .ai-chat-window {
    width: 300px;
    height: 400px;
  }
  
  .chat-toggle-btn {
    padding: 10px 16px;
  }
  
  .chat-icon {
    font-size: 18px;
  }
  
  .chat-text {
    font-size: 13px;
  }
  
  .product-card img {
    width: 60px;
    height: 60px;
  }
  
  .product-card h4 {
    font-size: 13px;
  }
  
  .product-card p {
    font-size: 12px;
  }
  
  .product-card a {
    font-size: 11px;
  }
}
</style>
